import { toast } from '@oy/uidesktop';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  addAChild,
  checkChildUsernameStatus,
  registerChildUserV2,
} from 'src/clients';
import { useEnv, useGetChildrenList, useTranslate } from 'src/hooks';
import { reportFunctionError } from 'src/utilities/sentry';
import { CheckUsernameResponseCode } from '../ChildrenList/constants';
import { RegisterChildFormType } from './Steps/RegisterChildStep/formSchema';
import { AddChildModalState, AddChildStep, FirstStepForm } from './constants';

export const useAddChildImpl = () => {
  const { getTranslation } = useTranslate();
  const navigate = useNavigate();
  const [step, setStep] = useState(AddChildStep.ChildType);
  const [modalState, setModalState] = useState(AddChildModalState.None);
  const [form, setForm] = useState<FirstStepForm>({});
  const { isProdEnv } = useEnv();
  const { total: totalChild = 0, mutateAny } = useGetChildrenList({
    fetchAll: true,
  });

  const disableNext = useMemo(() => {
    switch (step) {
      case AddChildStep.ChildType:
        return !form.childType || !form.legality || !form.username;
      default:
        return false;
    }
  }, [step, form.childType, form.legality, form.username]);

  const state = { disableNext, modalState, step, form, isProdEnv };

  const onCloseConfirmationModal = () => setModalState(AddChildModalState.None);

  const onClose = () => navigate(-1);

  const setFormState = (newState: Partial<FirstStepForm>) => {
    setForm(prev => ({ ...prev, ...newState }));
  };

  const generateStagingSubEntity = async () => {
    try {
      const res = await addAChild({
        username: '',
        childType: '',
        isCopyData: false,
      });
      if (res.data.success) {
        const { codeStatus, messageStatus } = res.data.data!;
        const REQUEST_SENT_CODE = '000';
        if (codeStatus === REQUEST_SENT_CODE) {
          toast.success(
            getTranslation(`MAMdemo_generateChild_message_success`),
          );
        } else {
          toast.error(messageStatus);
        }
      }
    } catch (exception) {
      reportFunctionError({
        domain: 'dashboard',
        message: 'Failed to generate staging sub entity',
        name: 'generateStagingSubEntity',
        exception,
      });
    } finally {
      onClose();
    }
  };

  const onClickNext = async () => {
    if (!isProdEnv) {
      if (totalChild > 0) {
        onClose();
        setModalState(AddChildModalState.ChildAlreadyGenerated);
        return;
      }
      await generateStagingSubEntity();
      return;
    }
    if (step !== 0) return;

    const res = await checkChildUsernameStatus({
      username: form.username!,
    });
    if (res.data.success) {
      const { codeStatus } = res.data.data!;
      switch (codeStatus) {
        case CheckUsernameResponseCode.USERNAME_AVAILABLE_TO_ADD:
          setModalState(AddChildModalState.ConfirmConnect);
          break;
        case CheckUsernameResponseCode.USERNAME_NOT_AVAILABLE_TO_ADD:
          setModalState(AddChildModalState.AlreadyHasRelation);
          break;
        case CheckUsernameResponseCode.ALREADY_REQUEST_TO_CONNECT:
          setModalState(AddChildModalState.RequestAlreadySent);
          break;
        case CheckUsernameResponseCode.USERNAME_DOES_NOT_EXIST:
          setStep(AddChildStep.RegisterChild);
          break;
        default:
          break;
      }
    }
  };

  const onClickConfirmConnect = async () => {
    try {
      const res = await addAChild({
        username: form.username!,
        childType: form.childType!,
        isCopyData: form.legality === 'true',
      });
      if (res.data.success) {
        const { codeStatus, messageStatus } = res.data.data!;
        const REQUEST_SENT_CODE = '000';
        if (codeStatus === REQUEST_SENT_CODE) {
          toast.success(
            `Request to make 
          ${form.username} as child has sent`,
          );
        } else {
          toast.error(messageStatus);
        }
        setModalState(AddChildModalState.None);
        await mutateAny();
        onClose();
      }
    } catch (exception) {
      const message = `Failed to make a request for ${form.username} as a child`;
      reportFunctionError({
        domain: 'dashboard',
        message,
        name: 'addChild',
        exception,
      });
    }
  };

  const onSubmitRegisterChild = async (params: RegisterChildFormType) => {
    const res = await registerChildUserV2({
      brand_name: params.brand_name,
      email: params.email,
      full_name: params.full_name,
      username: params.username,
      child_type: form.childType!,
      is_copy_data: form.legality === 'true',
      phone_number:
        params.country_code && params.phone_number
          ? params.country_code + params.phone_number
          : null,
    });
    if (res.data.status === 'OK') {
      toast.success(
        res.data.message ?? 'Success register child without the same legality',
      );
      await mutateAny();
      onClose();
    }
  };

  return {
    state,
    onSubmitRegisterChild,
    onClickConfirmConnect,
    onClickNext,
    setFormState,
    onCloseConfirmationModal,
    onClose,
    setStep,
  };
};
